import { makeAutoObservable } from "mobx";
import * as React from "react";
import { delay } from "src/common/delay";

import { CollapseList } from "../components/CollapseList";
import { FieldView } from "../components/FieldView";
import { GridView } from "../components/GridView";
import { TabsView } from "../components/TabsView";
import { MdmCardFormStore } from "../MdmCardFormStore";
import { createBoolResult } from "./calcBoolExpr";
import { createValidator } from "./createValidator";
import { ZDataNode, ZRecord } from "./dataTypes";
import { joinToPath, pathDiv } from "./fieldPath";
import {
  FnFocus,
  FormCollapseList,
  FormField,
  FormGrid,
  FormItem,
  FormRow,
  FormTabs,
} from "./MdmCardFormTypes";
import {
  ZCompCollapseList,
  ZMetaField,
  ZMetaGrid,
  ZMetaNode,
  ZMetaTabs,
} from "./metaTypes";

let currentKey = 0;
const generateKey = (): number => ++currentKey;

const createFormItemCommon = <T extends ZMetaNode>(p: {
  owner: FormItem | null;
  meta: T;
  form: MdmCardFormStore;
  disabledLocal?: () => boolean;
}): Pick<FormItem<T>, "key" | "meta" | "ownerKey"> => ({
  key: generateKey(),
  ownerKey: p.owner ? p.owner.key : null,
  meta: p.meta,
});

type FormItemCtx = {
  basePath: string;
  form: MdmCardFormStore;
};

export const createFormField = (
  owner: FormItem | null,
  meta: ZMetaField,
  ctx: FormItemCtx,
): FormField => {
  let onFocus: FnFocus;
  const field: FormField = {
    ...createFormItemCommon({ owner, meta, form: ctx.form }),
    activateSubItem: () => Promise.resolve(),
    focus: async () => {
      if (onFocus) await onFocus();
    },
    get isDisabled() {
      return boolRes ? boolRes.value : false;
    },
    path: joinToPath(ctx.basePath, meta.name),
    render(form) {
      return (
        <div title={field.path}>
          <FieldView
            form={form}
            field={field}
            focusBk={(fn) => {
              onFocus = fn;
            }}
          />
        </div>
      );
    },
    walk(visitor) {
      visitor.onItem?.(field);
      visitor.onField?.(field);
      visitor.onFieldEnd?.(field);
    },
    data: null,
    setData(node) {
      field.data = node;
    },
    validators: [],
  };
  const boolRes = meta.disableIf
    ? createBoolResult(meta.disableIf, field, ctx.form)
    : null;
  if (meta.rules) {
    field.validators = meta.rules.map((rule) =>
      createValidator(rule, field, ctx.form),
    );
  }
  return makeAutoObservable(field);
};

const createCollapseList = (
  owner: FormItem | null,
  meta: ZMetaField,
  component: ZCompCollapseList,
  ctx: FormItemCtx,
): FormCollapseList => {
  // eslint-disable-next-line no-param-reassign
  if (!meta.colSpan) meta.colSpan = "*";
  const field: FormCollapseList = {
    ...createFormItemCommon({ owner, meta, form: ctx.form }),
    activateSubItem: async () => {
      if (field.collapsed) {
        field.setCollapsed(false);
        await delay(20);
      }
      // Когда появится коллапс для строк, то надо будет искать строку и делать для неё expand
      // field.rows.find(({item}) => item.key === subItem.key);
    },
    focus: () => Promise.resolve(),
    path: joinToPath(ctx.basePath, meta.name),
    render(form) {
      return <CollapseList field={field} form={form} />;
    },
    walk(visitor) {
      visitor.onItem?.(field);
      visitor.onField?.(field);
      field.rows.forEach((row) => {
        row.item.walk(visitor);
      });
      visitor.onFieldEnd?.(field);
    },
    get data(): ZDataNode {
      return {
        name: meta.name,
        type: "recordArray",
        value: this.rows.map((row) => {
          const dstRow: ZRecord = [];
          row.item.walk({
            onField: (currentField) => {
              const pos = currentField.path.lastIndexOf(pathDiv);
              if (pos >= 0) {
                const base = currentField.path.slice(0, pos);
                if (base === row.basePath && currentField.data) {
                  dstRow.push(currentField.data);
                }
              }
            },
          });
          return dstRow;
        }),
      };
    },
    setData(dataNode) {
      const records: ZRecord[] =
        dataNode?.type === "recordArray" ? dataNode.value : [];
      const queue: [ZRecord, FormItem, string][] = [];
      const newRows: FormRow[] = records.map((curRec) => {
        const basePath = joinToPath(field.path, generateKey());
        const item = createFormItem(field, component.row.layout, {
          ...ctx,
          basePath,
        });
        queue.push([curRec, item, basePath]);
        const newRow: FormRow = {
          basePath,
          item,
        };
        return newRow;
      });
      this.rows = newRows;
      queue.forEach(([record, item, basePath]) => {
        item.walk({
          onField(currentField) {
            if (
              currentField.path === joinToPath(basePath, currentField.meta.name)
            ) {
              const fieldDataNode = record.find(
                ({ name }) => name === currentField.meta.name,
              );
              currentField.setData(fieldDataNode ?? null);
            }
          },
        });
      });
    },
    // setDataOld(dataNode) {
    //   field.data = node;
    //   // здесь нужно заполнить rows
    //   const records: ZRecord[] = node?.type === "recordArray" ? node.value : [];
    //   const { rows } = this;
    //   const newRows: FormRow[] = records.map((curRec) => {
    //     const oldRow = rows.find(({ record }) => curRec === record);
    //     if (oldRow) return oldRow;
    //     const basePath = joinToPath(field.path, generateKey()); // строка должна идентифицироваться уникальным ключом, т.к. индекс может меняться при удалении строки
    //     const newRow: FormRow = {
    //       record: curRec,
    //       item: createFormItem(field, component.row.layout, {
    //         basePath,
    //         form: ctx.form,
    //       }),
    //     };
    //     setTimeout(() => {
    //       // TODO: сохранить список обновляемых полей. И зызвать для них setData после обновления rows
    //       ctx.form.updateFields();
    //       curRec.forEach((it) => {
    //         const f = ctx.form.fields[joinToPath(basePath, it.name)];
    //         if (f) {
    //           f.setData(it);
    //         }
    //       });
    //     }, 1);
    //     return newRow;
    //   });
    //   this.rows = newRows;
    // },
    collapsed: false,
    setCollapsed(value) {
      this.collapsed = value;
    },
    activeItem: -1,
    setActiveItem(index: number) {
      this.activeItem = index;
    },
    rows: [],
    newRow() {
      const records: ZRecord[] =
        field.data?.type === "recordArray" ? [...field.data.value] : [];
      records.push([]);
      field.setData({
        name: meta.name,
        type: "recordArray",
        value: records,
      });
    },
    validators: [], // TODO: пока нет валидации
  };
  return makeAutoObservable(field);
};

export const createFormGrid = (
  owner: FormItem | null,
  meta: ZMetaGrid,
  ctx: FormItemCtx,
): FormGrid => {
  const grid: FormGrid = {
    ...createFormItemCommon({ owner, meta, form: ctx.form }),
    activateSubItem: () => Promise.resolve(),
    items: [],
    render(form) {
      return <GridView form={form} gridItem={grid} />;
    },
    walk(visitor) {
      visitor.onItem?.(grid);
      visitor.onGrid?.(grid);
      grid.items.forEach((it) => it.walk(visitor));
      visitor.onGridEnd?.(grid);
    },
  };
  grid.items = meta.items.map((it: ZMetaNode) => createFormItem(grid, it, ctx));
  return grid;
};

const createFormTabs = (
  owner: FormItem | null,
  meta: ZMetaTabs,
  ctx: FormItemCtx,
): FormTabs => {
  const layout: FormTabs = {
    ...createFormItemCommon({ owner, meta, form: ctx.form }),
    curKey: meta.tabs[0]?.key ?? "",
    setCurKey(key: string) {
      this.curKey = key;
    },
    tabs: {},
    walk(visitor) {
      visitor.onItem?.(layout);
      visitor.onTabs?.(layout);
      Object.values(layout.tabs).forEach((tabContent) =>
        tabContent.walk(visitor),
      );
    },
    render: (form) => <TabsView form={form} tabsItem={layout} />,
    async activateSubItem(subItem) {
      const pair = Object.entries(this.tabs).find(
        (it) => it[1].key === subItem.key,
      );
      if (pair) {
        this.setCurKey(pair[0]);
        await delay(50);
      }
    },
  };
  layout.tabs = meta.tabs.reduce(
    (dict, tab) => ({
      ...dict,
      [tab.key]: createFormItem(layout, tab.layout, ctx),
    }),
    {} as Record<string, FormItem>,
  );

  return makeAutoObservable(layout);
};

/* eslint consistent-return: "off" */

export const createFormItem = (
  owner: FormItem | null,
  meta: ZMetaNode,
  ctx: FormItemCtx,
): FormItem => {
  // eslint-disable-next-line default-case
  switch (meta.type) {
    case "field":
      if (meta.component.type === "CollapseList") {
        return createCollapseList(owner, meta, meta.component, ctx);
      }
      return createFormField(owner, meta, ctx);
    case "grid":
      return createFormGrid(owner, meta, ctx);
    case "tabs":
      return createFormTabs(owner, meta, ctx);
  }
};
